import React, { useContext, useEffect, useState } from 'react';
import { Col, Menu, Row } from 'antd';
import { NavLink, Route, Switch, useLocation } from 'react-router-dom';
import { UsersPage, UserPage } from './users';
import { RolePage, RolesPage } from './roles';
import { IntegrationPage, IntegrationsPage } from './integrations';
import { GridPage, GridsPage } from './grids';
import { SystemSettingsPage } from './system-settings';
import { UserGroupsPage } from './user-groups/user-groups-page';
import {
  ApiOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  ToolOutlined,
  UserOutlined,
  UserSwitchOutlined,
  HistoryOutlined,
  FieldTimeOutlined,
  BookOutlined,
  UsergroupAddOutlined,
  FilterOutlined,
  DeleteColumnOutlined,
} from '@ant-design/icons';
import { UserContext } from '../contexts';
import {
  hasAdminDelegationsPageRole,
  hasAdminGridsPageRole,
  hasAdminIntegrationsPageRole,
  hasAdminRolesPageRole,
  hasAdminSystemSettingsPageRole,
  hasAdminUsersPageRole,
  hasAdminCachesPageRole,
  hasAdminBackupsPageRole,
  hasAdminLogsPageRole,
  hasAdminDictionaryPageRole,
  hasAdminFailedQueueMessagesPageRole,
  hasAdminUserGroupsPageRole,
  hasAdminFiltersPageRole,
} from '../../utils';
import { DelegationsPage } from './delegations';
import { CachesPage } from './caches';
import { BackupsPage } from './backups';
import { LogsPage } from './logs';
import { DictionaryPage } from './dictionary';
import { UserGroupPage } from './user-groups/user-group-page';
import { Roles2Page, Role2Page } from './roles2';
import { FilterPage, FiltersPage } from './filters';
import { FailedQueueMessagesPage } from './queue';

export function AdminPage() {
  const { pathname } = useLocation();
  const [path, setPath] = useState('');
  const [links, setLinks] = useState<any[]>();
  const { user } = useContext(UserContext);

  useEffect(() => {
    let activePath = '/admin';
    if (pathname === activePath && links?.length) {
      activePath = links[0].path;
    } else {
      activePath += '/' + pathname.split('/')[2];
    }
    setPath(activePath);
  }, [pathname, links]);

  useEffect(() => {
    const permittedLinks = [];
    if (hasAdminUsersPageRole(user)) {
      permittedLinks.push({
        path: '/admin/users',
        text: 'Kullanıcılar',
        icon: <UserOutlined />,
        component: UsersPage,
      });
    }
    if (hasAdminUserGroupsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/user-groups',
        text: 'Kullanıcı grupları (yeni)',
        icon: <UsergroupAddOutlined />,
        component: UserGroupsPage,
      });
    }
    if (hasAdminRolesPageRole(user)) {
      permittedLinks.push({
        path: '/admin/roles',
        text: 'Roller',
        icon: <SafetyOutlined />,
        component: RolesPage,
      });
    }
    permittedLinks.push({
      path: '/admin/roles2',
      text: 'Roller (yeni)',
      icon: <SafetyOutlined />,
      component: Roles2Page,
    });
    if (hasAdminDelegationsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/delegations',
        text: 'Vekaletler',
        icon: <UserSwitchOutlined />,
        component: DelegationsPage,
      });
    }
    if (hasAdminGridsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/grids',
        text: 'Veri setleri',
        icon: <DatabaseOutlined />,
        component: GridsPage,
      });
    }
    if (hasAdminFiltersPageRole(user)) {
      permittedLinks.push({
        path: '/admin/filters',
        text: 'Filtreler (yeni)',
        icon: <FilterOutlined />,
        component: FiltersPage,
      });
    }
    if (hasAdminIntegrationsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/integrations',
        text: 'Entegrasyonlar',
        icon: <ApiOutlined />,
        component: IntegrationsPage,
      });
    }
    if (hasAdminSystemSettingsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/system-settings',
        text: 'Sistem ayarları',
        icon: <ToolOutlined />,
        component: SystemSettingsPage,
      });
    }
    if (hasAdminFailedQueueMessagesPageRole(user)) {
      permittedLinks.push({
        path: '/admin/failed-queue-messages',
        text: 'Kuyruk mesajları',
        icon: <DeleteColumnOutlined />,
        component: FailedQueueMessagesPage,
      });
    }
    if (hasAdminCachesPageRole(user)) {
      permittedLinks.push({
        path: '/admin/caches',
        text: "Cache'lenmiş veriler",
        icon: <ContainerOutlined />,
        component: CachesPage,
      });
    }
    if (hasAdminBackupsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/backups',
        text: 'Yedekler',
        icon: <HistoryOutlined />,
        component: BackupsPage,
      });
    }
    if (hasAdminLogsPageRole(user)) {
      permittedLinks.push({
        path: '/admin/logs',
        text: "Sistem log'ları",
        icon: <FieldTimeOutlined />,
        component: LogsPage,
      });
    }
    if (hasAdminDictionaryPageRole(user)) {
      permittedLinks.push({
        path: '/admin/dictionary',
        text: 'Sözlük',
        icon: <BookOutlined />,
        component: DictionaryPage,
      });
    }
    setLinks(permittedLinks);
  }, [user]);

  return (
    <Row>
      <Col span={6} xs={24} sm={24} md={6} lg={6} xl={5} xxl={4}>
        <Menu mode="inline" selectedKeys={[path]} style={{ paddingBottom: '3rem' }}>
          <Menu.ItemGroup title="Yönetim">
            {links?.map(l => (
              <Menu.Item key={l.path} icon={l.icon}>
                <NavLink to={l.path}>{l.text}</NavLink>
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        </Menu>
      </Col>
      <Col span={18} xs={24} sm={24} md={18} lg={18} xl={19} xxl={20}>
        <Switch>
          <Route exact path="/admin" component={links ? links[0].component : null} />
          <Route exact path="/admin/users" component={UsersPage} />
          <Route exact path="/admin/users/:id" component={UserPage} />
          <Route exact path="/admin/user-groups" component={UserGroupsPage} />
          <Route exact path="/admin/user-groups/:id" component={UserGroupPage} />
          <Route exact path="/admin/roles2" component={Roles2Page} />
          <Route exact path="/admin/roles2/:id" component={Role2Page} />
          <Route exact path="/admin/filters" component={FiltersPage} />
          <Route exact path="/admin/filters/:id" component={FilterPage} />
          <Route exact path="/admin/roles" component={RolesPage} />
          <Route exact path="/admin/roles/:id" component={RolePage} />
          <Route exact path="/admin/delegations" component={DelegationsPage} />
          <Route exact path="/admin/grids" component={GridsPage} />
          <Route exact path="/admin/grids/:id" component={GridPage} />
          <Route exact path="/admin/integrations" component={IntegrationsPage} />
          <Route exact path="/admin/integrations/:id" component={IntegrationPage} />
          <Route exact path="/admin/system-settings" component={SystemSettingsPage} />
          <Route exact path="/admin/caches" component={CachesPage} />
          <Route exact path="/admin/failed-queue-messages" component={FailedQueueMessagesPage} />
          <Route exact path="/admin/backups" component={BackupsPage} />
          <Route exact path="/admin/logs" component={LogsPage} />
          <Route exact path="/admin/dictionary" component={DictionaryPage} />
        </Switch>
      </Col>
    </Row>
  );
}
